import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * `<OnboardingFlowView />` — SHA v0 patient onboarding flow view per
 * [`sha_v0_patient_onboarding_flow_design.md`](../../../../sha_v0_patient_onboarding_flow_design.md)
 * §3 + §7.1.
 *
 * v0 scaffold (Sprint 7 close §7.1 scope per memo):
 *   - Subscribes to `IOnboardingFlow` state
 *   - Renders sub-state per `kind` switch (placeholder bodies for
 *     activation-pending / recovery-setup-election / recovery-setup-
 *     ceremony / first-session-bootstrap / initial-sign-in / terminal)
 *   - Recovery-setup-election surface offers tier election (v1 default
 *     'low-risk' per memo §3.2)
 *
 * **Out of scope (Sprint 8+):**
 *   - Substantive recovery-setup ceremony UI (composes with DEV3 wire-
 *     contract `IProjectionServiceClient.initiateRecoverySetup` etc.)
 *   - App.tsx flow gating wiring (defer to next composition cycle;
 *     this scaffold is purely additive — new files only)
 *   - Substantive nested rendering of activation/auth views (this view
 *     is a wrapper; existing `<ActivationView />` + `<AuthenticationView />`
 *     remain canonical for those sub-flows)
 *
 * **Doctrinal anchors (memo §6 binding):**
 *   - Recovery UX honesty (Doctrine v1.3 §8 B.3 ELEVATE) — recovery-
 *     setup-election surface preserves "content unrecoverable" framing
 *     as recovery-failure consequence; v0 placeholder copy gates at
 *     PFT v1.3 doctrinal-copy gate
 *   - Pattern B no-parallel-rendering-surface — view is client-side
 *     rendering; substrate provides projection data via API
 */
import * as React from 'react';
import { useLocale } from '../i18n/locale-context.js';
function useOnboardingFlowState(flow) {
    const [state, setState] = React.useState(() => flow.getState());
    React.useEffect(() => {
        return flow.subscribe(setState);
    }, [flow]);
    return state;
}
export function OnboardingFlowView(props) {
    const { t } = useLocale();
    if (props.flow === undefined) {
        return (_jsxs("section", { "aria-labelledby": "onboarding-preparing-heading", "aria-busy": "true", "data-testid": "onboarding-flow-view", children: [_jsx("h2", { id: "onboarding-preparing-heading", children: t('onboarding.preparing_heading') }), _jsx("p", { role: "status", children: t('onboarding.preparing_body') })] }));
    }
    return _jsx(OnboardingFlowViewBody, { flow: props.flow });
}
function OnboardingFlowViewBody(props) {
    const { t } = useLocale();
    const state = useOnboardingFlowState(props.flow);
    return (_jsxs("section", { "aria-labelledby": "onboarding-heading", "data-testid": "onboarding-flow-view", "data-state": state.kind, children: [_jsx("h2", { id: "onboarding-heading", children: t('onboarding.welcome_heading') }), _jsx(OnboardingFlowSubView, { state: state, flow: props.flow })] }));
}
function OnboardingFlowSubView(props) {
    const { t } = useLocale();
    const { state, flow } = props;
    switch (state.kind) {
        case 'activation-pending':
            return (_jsx("div", { "data-testid": "onboarding-sub-activation-pending", children: _jsx("p", { children: t('onboarding.activation_pending_body') }) }));
        case 'recovery-setup-election':
            return (_jsxs("div", { "data-testid": "onboarding-sub-recovery-setup-election", children: [_jsx("p", { children: t('onboarding.recovery_setup_election_body') }), _jsx("button", { type: "button", onClick: () => flow.submitRecoveryTier('low-risk'), "data-testid": "onboarding-recovery-tier-low-risk", children: t('onboarding.recovery_tier_low_risk_cta') })] }));
        case 'recovery-setup-ceremony':
            return (_jsxs("div", { "data-testid": "onboarding-sub-recovery-setup-ceremony", "data-tier": state.tier, children: [_jsx("p", { children: t('onboarding.recovery_setup_ceremony_body') }), _jsx("button", { type: "button", onClick: () => flow.notifyRecoverySetupComplete(), "data-testid": "onboarding-recovery-setup-complete", children: t('onboarding.recovery_setup_ceremony_complete_cta') })] }));
        case 'first-session-bootstrap':
            return (_jsxs("div", { "data-testid": "onboarding-sub-first-session-bootstrap", children: [_jsx("p", { children: t('onboarding.first_session_bootstrap_body') }), _jsx("button", { type: "button", onClick: () => flow.notifyBootstrapComplete(), "data-testid": "onboarding-bootstrap-complete", children: t('onboarding.first_session_bootstrap_complete_cta') })] }));
        case 'initial-sign-in':
            return (_jsx("div", { "data-testid": "onboarding-sub-initial-sign-in", children: _jsx("p", { children: t('onboarding.initial_sign_in_body') }) }));
        case 'account-home-landing':
            return (_jsx("div", { "data-testid": "onboarding-sub-account-home-landing", role: "status", children: _jsx("p", { children: t('onboarding.account_home_landing_body') }) }));
        case 'cancelled':
            return (_jsx("div", { "data-testid": "onboarding-sub-cancelled", children: _jsx("p", { children: t('onboarding.cancelled_body') }) }));
        case 'error':
            return (_jsxs("div", { "data-testid": "onboarding-sub-error", role: "alert", children: [_jsx("p", { children: t('onboarding.error_body') }), _jsx("p", { children: state.message })] }));
    }
}
//# sourceMappingURL=onboarding-flow-view.js.map